import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Code as CodeIcon,
  PlayArrow as RunIcon,
  Lightbulb as LightbulbIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { strategyService, Strategy } from '../../services/strategyService';

// Available variables will be loaded from API

const OPERATORS = ['>', '<', '>=', '<=', '==', '!=', 'and', 'or', 'not', 'in', 'not in'];

const EXAMPLE_STRATEGIES = [
  {
    name: 'Value Stocks',
    query: 'pe_ratio < 15 and pb_ratio < 1.5 and debt_to_equity < 0.5 and roe > 15',
    description: 'Find undervalued stocks with good fundamentals'
  },
  {
    name: 'Golden Crossover Momentum',
    query: 'gco_change > 0 and gco_hit_rate > 60 and gco_count > 5 and day_return > 2',
    description: 'Stocks with recent golden crossover and positive momentum'
  },
  {
    name: 'High Growth',
    query: 'one_year_return > 50 and month_return > 10 and market_cap > 1000',
    description: 'High-growth large-cap stocks'
  },
  {
    name: 'Swing Trading Candidates',
    query: 'n_swing_10 > 10 and rsi < 30 and last_trade_price < one_year_high * 0.8',
    description: 'Stocks good for swing trading, currently oversold'
  },
  {
    name: 'Dividend Aristocrats',
    query: 'dividend_yield > 3 and pe_ratio < 20 and debt_to_equity < 1 and one_year_return > 0',
    description: 'Stable dividend-paying stocks'
  }
];

interface StrategyEditorProps {
  open: boolean;
  onClose: () => void;
  strategy: Strategy | null;
  onSave: (strategy: Strategy) => void;
}

const StrategyEditor: React.FC<StrategyEditorProps> = ({ open, onClose, strategy, onSave }) => {
  const [query, setQuery] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [testResults, setTestResults] = useState<any>(null);
  const [testing, setTesting] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [availableVariables, setAvailableVariables] = useState<{[key: string]: {type: string, description: string}}>({});
  const [variablesLoading, setVariablesLoading] = useState(false);
  
  const textAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (strategy) {
      setName(strategy.name);
      setDescription(strategy.description || '');
      setQuery(strategy.query);
    } else {
      setName('');
      setDescription('');
      setQuery('');
    }
  }, [strategy]);

  // Load available variables from API
  useEffect(() => {
    if (open) {
      loadAvailableVariables();
    }
  }, [open]);

  const loadAvailableVariables = async () => {
    try {
      setVariablesLoading(true);
      const variables = await strategyService.getAvailableVariables();
      
      // Flatten the nested structure
      const flatVariables: {[key: string]: {type: string, description: string}} = {};
      Object.values(variables).forEach(category => {
        Object.entries(category).forEach(([key, value]) => {
          flatVariables[key] = value;
        });
      });
      
      setAvailableVariables(flatVariables);
    } catch (error) {
      console.error('Failed to load available variables:', error);
      toast.error('Failed to load available variables');
    } finally {
      setVariablesLoading(false);
    }
  };

  const handleQueryChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    const position = event.target.selectionStart;
    
    setQuery(value);
    setCursorPosition(position);
    
    // Get suggestions based on current word
    const words = value.substring(0, position).split(/\s+/);
    const currentWord = words[words.length - 1];
    
    if (currentWord.length > 0) {
      const variableSuggestions = Object.keys(availableVariables)
        .filter(variable => variable.toLowerCase().includes(currentWord.toLowerCase()))
        .slice(0, 10);
      
      const operatorSuggestions = OPERATORS
        .filter(op => op.toLowerCase().includes(currentWord.toLowerCase()))
        .slice(0, 5);
      
      setSuggestions([...variableSuggestions, ...operatorSuggestions]);
      setShowSuggestions(variableSuggestions.length > 0 || operatorSuggestions.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const insertSuggestion = (suggestion: string) => {
    const words = query.substring(0, cursorPosition).split(/\s+/);
    const currentWord = words[words.length - 1];
    const beforeCursor = query.substring(0, cursorPosition - currentWord.length);
    const afterCursor = query.substring(cursorPosition);
    
    const newQuery = beforeCursor + suggestion + afterCursor;
    setQuery(newQuery);
    setShowSuggestions(false);
    
    // Focus back to textarea
    if (textAreaRef.current) {
      const textArea = textAreaRef.current.querySelector('textarea');
      if (textArea) {
        textArea.focus();
        const newPosition = beforeCursor.length + suggestion.length;
        setTimeout(() => {
          textArea.setSelectionRange(newPosition, newPosition);
        }, 0);
      }
    }
  };

  const handleTestStrategy = async () => {
    if (!query.trim()) {
      toast.error('Please enter a strategy query');
      return;
    }

    try {
      setTesting(true);
      const results = await strategyService.testStrategy(query);
      setTestResults(results);
      toast.success(`Strategy test completed: ${results.count} stocks found`);
    } catch (error: any) {
      console.error('Strategy test failed:', error);
      toast.error('Strategy test failed: ' + error.message);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim() || !query.trim()) {
      toast.error('Please provide both name and query');
      return;
    }

    try {
      const strategyData = {
        name: name.trim(),
        description: description.trim(),
        query: query.trim(),
        is_active: true,
        notification_enabled: true,
        execution_frequency: 'daily' as const,
      };

      let savedStrategy: Strategy;
      if (strategy) {
        savedStrategy = await strategyService.updateStrategy(strategy.id, strategyData);
      } else {
        savedStrategy = await strategyService.createStrategy(strategyData);
      }

      onSave(savedStrategy);
    } catch (error: any) {
      console.error('Failed to save strategy:', error);
      toast.error('Failed to save strategy: ' + error.message);
    }
  };

  const loadExample = (example: typeof EXAMPLE_STRATEGIES[0]) => {
    setName(example.name);
    setDescription(example.description);
    setQuery(example.query);
    setShowExamples(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CodeIcon />
            <Typography variant="h6">
              {strategy ? 'Edit Strategy' : 'Create Strategy'}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Show Examples">
              <IconButton onClick={() => setShowExamples(!showExamples)}>
                <LightbulbIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Basic Information */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Strategy Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>

          {/* Examples Panel */}
          {showExamples && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Example Strategies
                </Typography>
                <Grid container spacing={2}>
                  {EXAMPLE_STRATEGIES.map((example, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper sx={{ p: 2, cursor: 'pointer' }} onClick={() => loadExample(example)}>
                        <Typography variant="subtitle2" gutterBottom>
                          {example.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          {example.description}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                          {example.query}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Query Editor */}
          <Box position="relative">
            <Typography variant="h6" gutterBottom>
              Strategy Query
            </Typography>
            <TextField
              ref={textAreaRef}
              value={query}
              onChange={handleQueryChange}
              multiline
              rows={8}
              fullWidth
              placeholder="Enter your strategy query here...
Example: market_cap > 1000 and pe_ratio < 15 and one_year_return > 20"
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                }
              }}
            />
            
            {/* Autocomplete Suggestions */}
            {showSuggestions && suggestions.length > 0 && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <List dense>
                  {suggestions.map((suggestion, index) => (
                    <ListItem
                      key={index}
                      button
                      onClick={() => insertSuggestion(suggestion)}
                    >
                      <ListItemText
                        primary={suggestion}
                        secondary={availableVariables[suggestion]?.description || 'Operator'}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Available Variables */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Available Variables
              </Typography>
              {variablesLoading ? (
                <Typography>Loading variables...</Typography>
              ) : (
                <>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2} sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {Object.entries(availableVariables).map(([variable, info]) => (
                      <Tooltip key={variable} title={info.description}>
                        <Chip
                          label={variable}
                          size="small"
                          onClick={() => {
                            const newQuery = query + (query.endsWith(' ') ? '' : ' ') + variable;
                            setQuery(newQuery);
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Click on variables to add them to your query. Hover for descriptions. ({Object.keys(availableVariables).length} variables available)
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>

          {/* Test Results */}
          {testResults && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Test Results
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Found {testResults.count} stocks matching your criteria
                </Typography>
                {testResults.sample && testResults.sample.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Sample Results:
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {testResults.sample.slice(0, 10).map((stock: any, index: number) => (
                        <Chip key={index} label={stock.symbol} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Query Syntax:</strong> Use Python-like expressions with available variables.
              Examples: <code>pe_ratio &lt; 15</code>, <code>market_cap &gt; 1000 and roe &gt; 15</code>
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleTestStrategy}
          disabled={testing || !query.trim()}
          startIcon={testing ? <RunIcon /> : <RunIcon />}
        >
          {testing ? 'Testing...' : 'Test Strategy'}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!name.trim() || !query.trim()}
        >
          {strategy ? 'Update' : 'Create'} Strategy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StrategyEditor;
